import { z } from 'zod';

/**
 * Zod schemas for operations-related entities (Turns, Inspections)
 * These provide runtime validation and type inference
 */

// Turn task schema (for nested tasks in Turn)
const turnTaskItemSchema = z.object({
  id: z.number(),
  label: z.string(),
  completed: z.boolean(),
});

// Turn schema - supports both API shape (startDate, targetDate) and UI shape (unit, property, moveOut, etc.)
export const turnSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  status: z.enum(['Pre-Flight', 'In Progress', 'Punch List', 'Marketing Ready', 'Scheduled', 'Ready', 'Completed'] as const),
  startDate: z.string().nullish(),
  targetDate: z.string().nullish(),
  unitId: z.string().nullish(),
  unit: z.string().nullish(),
  property: z.string().nullish(),
  moveOut: z.string().nullish(),
  daysVacant: z.number().int().nonnegative().nullish(),
  spent: z.number().nonnegative().nullish(),
  budget: z.number().nonnegative().nullish(),
  priority: z.enum(['Normal', 'High', 'Urgent', 'Critical'] as const).nullish(),
  tasks: z.union([z.array(turnTaskItemSchema), z.array(z.any())]).nullish(),
  sopId: z.string().nullish(),
  sopInstanceId: z.string().nullish(),
  completedAt: z.string().nullish(),
  actualCost: z.number().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
}).passthrough();

// Export schema only, types are in turns.ts

// Inspection finding schema (exported for API validation)
export const inspectionFindingSchema = z.object({
  id: z.string(),
  category: z.string(),
  item: z.string(),
  status: z.enum(['pass', 'fail', 'na'] as const),
  notes: z.string().optional(),
});

// Inspection remediation history schema
const inspectionRemediationHistorySchema = z.object({
  findingId: z.string(),
  remediatedAt: z.string(),
  workOrderId: z.string().optional(),
  notes: z.string(),
});

// Inspection schema
export const inspectionSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  unitId: z.string().nullish(),
  type: z.enum(['Move-In', 'Move-Out', 'Annual', 'Routine'] as const),
  date: z.string(),
  status: z.enum(['Scheduled', 'In Progress', 'Completed', 'Flagged'] as const),
  inspector: z.string(),
  photos: z.array(z.string()).nullish(),
  unit: z.string().nullish(),
  property: z.string().nullish(),
  score: z.string().nullish(),
  findings: z.array(inspectionFindingSchema).nullish(),
  complianceType: z.enum(['HCV UPCS', 'Fair Housing', 'Fire Safety', 'General'] as const).nullish(),
  hcvProgramId: z.string().nullish(),
  remediationHistory: z.array(inspectionRemediationHistorySchema).nullish(),
  sopId: z.string().nullish(),
  sopInstanceId: z.string().nullish(),
}).passthrough();

/** Recurring compliance inspection schedules (distinct from single `Inspection` records). */
export const inspectionScheduleSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  unitId: z.string().optional(),
  type: z.enum(['Move-In', 'Move-Out', 'Annual', 'Routine'] as const),
  frequency: z.enum(['one-time', 'monthly', 'quarterly', 'annually'] as const),
  startDate: z.string(),
  nextDueDate: z.string(),
  assignedTo: z.string().optional(),
  reminderDays: z.array(z.number().int()),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  lastRunAt: z.string().optional(),
});

// Export schema only, types are in inspections.ts
